import fuzzysort from "fuzzysort";
import { useCallback, useMemo } from "preact/hooks";

import type { Category, Schedule } from "../types";
import { ScheduleByPlace } from "../components/schedules/ScheduleByPlace";
import { ScheduleByTime } from "../components/schedules/ScheduleByTime";
import { DaySelector } from "../components/ui/DaySelector";
import { FilterDropdown } from "../components/ui/FilterDropdown";
import { SearchInput } from "../components/ui/SearchInput";
import { DaySelectorSkeleton, FilterDropdownSkeleton, ScheduleItemSkeleton, TabsSkeleton } from "../components/ui/Skeleton";
import { TabPanel, Tabs } from "../components/ui/Tabs";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { useUrlStringState, useUrlArrayState } from "../hooks/useUrlState";
import { useSchedules } from "../hooks/useWidgetQueries";
import { dayjs, getDisplayDate } from "../lib/dateUtils";
import { getLocalizedName } from "../lib/localization";
import { hasAnySelectedCategory, matchesConfiguredCategory } from "../lib/categoryFilter";
import { useNavigation } from "../lib/router";

type SchedulesPageProps = {
  onNavigate: (path: string, elementId?: string) => void;
};

export function SchedulesPage({ onNavigate }: SchedulesPageProps) {
  const {
    edition,
    editionId,
    defaultView,
    locale,
    lockedCategoryIds,
    categoryName: configCategoryName,
  } = useEdition();
  const t = useTranslations();
  const { toArtist, toEvent } = useNavigation();
  const { data: schedules, loading } = useSchedules(editionId);

  const VIEW_TABS = [
    { id: "by-place", label: t.byStage },
    { id: "by-time", label: t.byTime },
  ];

  const [view, setView] = useUrlStringState("view", defaultView);
  const [selectedDay, setSelectedDay] = useUrlStringState("day", null);
  const [search, setSearch] = useUrlStringState("search", null);
  const [selectedCategories, setSelectedCategories] = useUrlArrayState("categories", []);

  // Get unique days from the schedules shown by this block.
  const days = useMemo(() => {
    if (!schedules || !edition) return [];

    const scheduledDays = new Set<string>();

    for (const schedule of schedules) {
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(schedule.categories, lockedCategoryIds)) {
          continue;
        }
      } else if (configCategoryName) {
        if (
          !schedule.categories.some((cat) =>
            matchesConfiguredCategory(
              cat,
              configCategoryName,
              locale,
              edition.default_locale_code
            )
          )
        ) {
          continue;
        }
      }

      scheduledDays.add(
        getDisplayDate(
          schedule.start_date,
          schedule.start_time,
          edition.day_start_time ?? null,
          edition.start_date ?? null,
        )
      );
    }

    return Array.from(scheduledDays).sort();
  }, [schedules, edition, lockedCategoryIds, configCategoryName, locale]);

  // Calculate default day: today if within edition range, otherwise first day
  const defaultDay = useMemo(() => {
    if (!days.length) return null;
    const today = dayjs().locale(locale).format("YYYY-MM-DD");
    return days.includes(today) ? today : days[0];
  }, [days, locale]);

  // Active day is URL param or default
  const activeDay = selectedDay && days.includes(selectedDay) ? selectedDay : defaultDay;

  // Get unique categories from all schedules
  const categories = useMemo(() => {
    if (!schedules) return [];
    const catMap = new Map<string, Category>();
    for (const schedule of schedules) {
      for (const cat of schedule.categories) {
        if (!catMap.has(cat._id)) {
          catMap.set(cat._id, cat as Category);
        }
      }
    }
    return Array.from(catMap.values()).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
  }, [schedules]);

  // Category options for FilterDropdown
  const categoryOptions = useMemo(() => {
    return categories.map((cat) => ({
      id: cat._id,
      label: getLocalizedName(cat, locale, edition?.default_locale_code) ?? cat.name ?? "",
    }));
  }, [categories, locale, edition?.default_locale_code]);

  // Handler that omits URL param when selecting default day
  const handleDayChange = useCallback(
    (day: string | null) => {
      setSelectedDay(day === defaultDay ? null : day);
    },
    [defaultDay, setSelectedDay],
  );

  // Filter schedules by selected day, category, and search
  const filteredSchedules = useMemo(() => {
    if (!schedules || !activeDay) return [];

    // Filter by day and category
    let filtered = schedules.filter((schedule) => {
      const displayDate = getDisplayDate(
        schedule.start_date,
        schedule.start_time,
        edition?.day_start_time ?? null,
        edition?.start_date ?? null,
      );
      if (displayDate !== activeDay) return false;

      // Block-configured category filter takes precedence (filter by ID, OR)
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(schedule.categories, lockedCategoryIds)) {
          return false;
        }
      } else if (configCategoryName) {
        // Legacy fallback for older blocks/shortcodes using category name
        if (
          !schedule.categories.some((cat) =>
            matchesConfiguredCategory(
              cat,
              configCategoryName,
              locale,
              edition?.default_locale_code
            )
          )
        ) {
          return false;
        }
      } else if (selectedCategories.length > 0) {
        // URL category filter (multi-select by ID)
        if (!hasAnySelectedCategory(schedule.categories, selectedCategories)) {
          return false;
        }
      }

      return true;
    });

    // Apply fuzzy search
    if (search) {
      const results = fuzzysort.go(search, filtered, {
        key: (s: Schedule) =>
          s.artist?.name ?? getLocalizedName(s.event, locale) ?? "",
        threshold: -10000,
      });
      return results.map((r) => r.obj);
    }

    return filtered;
  }, [schedules, activeDay, edition, search, selectedCategories, lockedCategoryIds, configCategoryName, locale]);

  const handleArtistClick = (artistId: string, elementId?: string) => {
    onNavigate(toArtist(artistId), elementId);
  };

  const handleEventClick = (eventId: string, elementId?: string) => {
    onNavigate(toEvent(eventId), elementId);
  };

  // Check if any filters are active (excluding day since it always has a value)
  const hasActiveFilters = (search && search.length > 0) || selectedCategories.length > 0;

  // Clear all filters
  const clearFilters = () => {
    setSearch(null);
    setSelectedCategories([]);
  };

  if (loading) {
    return (
      <div className="festapp-schedules-page">
        <div className="festapp-schedules-header">
          <SearchInput
            value=""
            onChange={() => {}}
            placeholder={t.searchSchedules}
            disabled
          />
          <div className="festapp-schedules-filter-row">
            <DaySelectorSkeleton />
            {lockedCategoryIds.length === 0 && !configCategoryName && <FilterDropdownSkeleton />}
          </div>
          <TabsSkeleton />
        </div>
        <div className="festapp-schedules-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <ScheduleItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="festapp-schedules-page">
      <div className="festapp-schedules-header">
        <SearchInput
          value={search ?? ""}
          onChange={(v) => setSearch(v || null)}
          placeholder={t.searchSchedules}
        />
        <div className="festapp-schedules-filter-row">
          <DaySelector
            days={days}
            selectedDay={activeDay}
            onChange={handleDayChange}
            showAll={false}
          />
          {categories.length > 0 && lockedCategoryIds.length === 0 && !configCategoryName && (
            <FilterDropdown
              label={t.categories}
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
            />
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="festapp-btn festapp-btn-ghost festapp-btn-sm"
              onClick={clearFilters}
            >
              {t.clearFilters}
            </button>
          )}
        </div>
        <Tabs
          tabs={VIEW_TABS}
          activeTab={view ?? defaultView}
          onChange={setView}
        />
      </div>

      <TabPanel id="by-place" activeTab={view ?? defaultView}>
        <ScheduleByPlace
          schedules={filteredSchedules}
          onArtistClick={handleArtistClick}
          onEventClick={handleEventClick}
        />
      </TabPanel>

      <TabPanel id="by-time" activeTab={view ?? defaultView}>
        <ScheduleByTime
          schedules={filteredSchedules}
          onArtistClick={handleArtistClick}
          onEventClick={handleEventClick}
        />
      </TabPanel>
    </div>
  );
}
